import React, { useState, useRef } from 'react';
import { GripVertical } from 'lucide-react';
import './KanbanBoard.css';

/**
 * A reusable Kanban board with drag-and-drop support.
 *
 * Props:
 *   columns        - Array of { id, title, color, items, droppable }
 *                     `id`    : unique column identifier (string or number)
 *                     `title` : display name shown in the column header
 *                     `color` : CSS class suffix for the header gradient
 *                               e.g. 'primary' -> kanban-header-primary
 *                     `items` : array of items to display as cards in this column
 *                     `droppable` : set to false for a display-only column that
 *                               cards can be dragged out of but not into
 *                               (defaults to true)
 *
 *   onCardMove     - (cardId, fromColumnId, toColumnId) => void
 *                     Called when a card is dragged from one column to another.
 *
 *   renderCard     - (item) => ReactNode
 *                     Render function for each card's content area.
 *
 *   renderColumnHeader - (column) => ReactNode   (optional)
 *                     Custom render for the column header. If omitted, the
 *                     default header shows the column title and item count.
 *
 *   getItemId      - (item) => string|number      (optional, defaults to item.id)
 *                     Extracts the unique id from an item.
 *
 *   getItemColumnId - (item) => string|number      (optional)
 *                     Returns the column id that the item currently belongs to.
 *                     Used to prevent no-op drops. If omitted, the board infers
 *                     column membership from which column the item is rendered in.
 *
 *   canDrag        - boolean | (item) => boolean (default true)
 *                     Whether cards can be dragged. If a function, called per-card.
 *
 *   emptyMessage   - string (default 'Drop items here' / 'No items')
 *                     Text shown when a column has no cards.
 *
 *   className      - string (optional)
 *                     Additional CSS class(es) for the board container.
 */
function KanbanBoard({
  columns,
  onCardMove,
  renderCard,
  renderColumnHeader,
  getItemId = (item) => item.id,
  getItemColumnId,
  canDrag = true,
  emptyMessage,
  className = '',
}) {
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedFromColumn, setDraggedFromColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragCounter = useRef({});

  // --- Drag handlers ---

  const handleDragStart = (e, item, columnId) => {
    setDraggedItem(item);
    setDraggedFromColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', getItemId(item));
    // Slight delay so the browser captures the element before adding the class
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDraggedFromColumn(null);
    setDragOverColumn(null);
    dragCounter.current = {};
  };

  const handleDragEnter = (e, columnId, droppable) => {
    e.preventDefault();
    dragCounter.current[columnId] = (dragCounter.current[columnId] || 0) + 1;

    if (!draggedItem) return;

    // Determine the source column of the dragged item
    const sourceColumn = getItemColumnId
      ? getItemColumnId(draggedItem)
      : draggedFromColumn;

    if (droppable && sourceColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragLeave = (e, columnId) => {
    dragCounter.current[columnId] = (dragCounter.current[columnId] || 0) - 1;
    if (dragCounter.current[columnId] <= 0) {
      dragCounter.current[columnId] = 0;
      if (dragOverColumn === columnId) {
        setDragOverColumn(null);
      }
    }
  };

  const handleDragOver = (e, droppable) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = droppable ? 'move' : 'none';
  };

  const handleDrop = (e, columnId, droppable) => {
    e.preventDefault();
    dragCounter.current[columnId] = 0;
    setDragOverColumn(null);

    const sourceColumn = draggedItem && getItemColumnId
      ? getItemColumnId(draggedItem)
      : draggedFromColumn;

    if (draggedItem && droppable && sourceColumn !== columnId) {
      onCardMove(getItemId(draggedItem), sourceColumn, columnId);
    }
    setDraggedItem(null);
    setDraggedFromColumn(null);
  };

  // --- Default column header ---

  const defaultColumnHeader = (column) => (
    <>
      <div className="kanban-column-title">
        <span className="kanban-stage-name">{column.title}</span>
        <span className="kanban-stage-count">{column.items.length}</span>
      </div>
    </>
  );

  // --- Render ---

  // canDrag can be a boolean or a function (item) => boolean
  const canDragAny = typeof canDrag === 'function' ? true : canDrag;
  const canDragItem = typeof canDrag === 'function' ? canDrag : () => canDrag;
  const resolvedEmptyMsg = emptyMessage || (canDragAny ? 'Drop items here' : 'No items');

  return (
    <div className={`kanban-board ${className}`.trim()} role="region" aria-label="Kanban board">
      {columns.map((column) => {
        const droppable = column.droppable !== false;
        return (
          <div
            key={column.id}
            className={`kanban-column ${dragOverColumn === column.id ? 'drag-over' : ''}`}
            {...(canDragAny
              ? {
                  onDragEnter: (e) => handleDragEnter(e, column.id, droppable),
                  onDragLeave: (e) => handleDragLeave(e, column.id),
                  onDragOver: (e) => handleDragOver(e, droppable),
                  onDrop: (e) => handleDrop(e, column.id, droppable),
                }
              : {})}
          >
            <div className={`kanban-column-header kanban-header-${column.color || 'primary'}`}>
              {renderColumnHeader ? renderColumnHeader(column) : defaultColumnHeader(column)}
            </div>

            <div className="kanban-column-body">
              {column.items.map((item) => {
                const itemId = getItemId(item);
                const isDragging = draggedItem && getItemId(draggedItem) === itemId;
                const itemDraggable = canDragItem(item);
                return (
                  <div
                    key={itemId}
                    className={`kanban-card ${isDragging ? 'dragging' : ''}`}
                    draggable={itemDraggable}
                    onDragStart={itemDraggable ? (e) => handleDragStart(e, item, column.id) : undefined}
                    onDragEnd={itemDraggable ? handleDragEnd : undefined}
                  >
                    {itemDraggable && (
                      <div className="kanban-card-drag-handle" aria-hidden="true">
                        <GripVertical size={14} />
                      </div>
                    )}
                    <div className="kanban-card-content">
                      {renderCard(item, column)}
                    </div>
                  </div>
                );
              })}
              {column.items.length === 0 && (
                <div className="kanban-empty-column">
                  {column.emptyMessage || (droppable ? resolvedEmptyMsg : 'Nothing here')}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default KanbanBoard;
